/**
 * useSprintBoard — the React binding for the Sprint Board surface (/work/sprint).
 *
 * Strict layering: the page calls THIS hook; the hook calls {@link SprintStore},
 * {@link TaskStore}, {@link TeamStore}, {@link TaskEstimateStore} and
 * {@link HistoricalStore}; the stores call the api facade. The page never
 * imports a store or the api directly.
 *
 * Resolution: given an explicit `sprintId` it loads that sprint; otherwise it
 * resolves the ACTIVE ROOM's board sprint — the 'active' sprint if one exists,
 * else the most recent 'planning' sprint (which the page can Start). This is the
 * single source of "the current sprint" for the board.
 *
 * The board reads {@link Task.boardStatus} (TODO / IN PROGRESS / DONE) and treats
 * `blocked` as an independent overlay (never a column). Moving a task to DONE is
 * routed through {@link completeTask} so the Learn phase can capture actual
 * effort and silently record a {@link HistoricalEstimate}.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useActiveRoom } from '../rooms/RoomContext';
import { SprintStore } from './stores/sprintStore';
import { TaskStore } from '../tasks/taskStore';
import { TeamStore } from '../teams/teamStore';
import { HistoricalStore } from './stores/historicalStore';
import { TaskEstimateStore } from './estimate/taskEstimateStore';
import {
  sortSprintsByRecency,
  sprintDayInfo,
  sprintWorkingDays,
  commonReductionDays as sumCommonReductionDays,
  memberOncallDays,
} from './models/sprintboardModel';
import { sprintProgress, type SprintProgress } from './engine/aggregation';
import {
  capacitySummary,
  type CapacityMember,
  type CapacitySummary,
} from './engine/capacity';
import {
  isSubtask,
  subtaskProgressByParent,
  topLevelTasks,
  totalEffortDays,
  type SubtaskProgress,
} from '../tasks/taskModel';
import {
  buildSprintInsights,
  type SprintInsights,
} from './insights/sprintInsights';
import {
  buildHistoricalDraft,
  buildSprintCompletion,
  type SprintCompletionSummary,
} from './insights/sprintCompletion';
import {
  completionNarrative,
  insightsNarrative,
} from './insights/sprintNarrative';
import type { Sprint } from './models/types';
import type { TaskEstimate } from './estimate/types';
import type { Blocker, BoardStatus, Task, TaskDraft } from '../tasks/types';
import type { TeamMember } from '../teams/types';

/** Today's local date as yyyy-mm-dd (safe to lexically compare with ISO dates). */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const EMPTY_COLUMNS: Record<BoardStatus, Task[]> = {
  TODO: [],
  IN_PROGRESS: [],
  DONE: [],
};

export interface UseSprintBoardResult {
  /** The resolved board sprint, or null when the room has no sprints. */
  sprint: Sprint | null;
  /** The sprint's tasks. */
  tasks: Task[];
  /**
   * The room's backlog: non-deleted, TOP-LEVEL tasks with no sprint assignment.
   * Powers the "Add from backlog" picker on the board.
   */
  backlog: Task[];
  /**
   * True when the resolved sprint is ACTIVE and its start date is in the past —
   * i.e. work added now is mid-sprint (unplanned) scope. Used to default the
   * ad-hoc flag when adding/creating tasks on the board.
   */
  sprintStarted: boolean;
  /** Tasks grouped into the three board lanes. */
  columns: Record<BoardStatus, Task[]>;
  /**
   * Subtask roll-up (done/total) keyed by parent task id, computed over the
   * sprint's tasks. Powers the parent card's subtask badge. Only parents that
   * have ≥1 subtask appear.
   */
  subtaskProgress: Map<string, SubtaskProgress>;
  /** Members of the sprint's linked team (empty when none). */
  members: TeamMember[];
  /** Working days (Mon–Fri) in the whole sprint. */
  workingDays: number;
  /**
   * Full bandwidth-based capacity summary for the sprint (team capacity =
   * sum of member bandwidth; per-engineer occupied vs bandwidth). Powers the
   * By-Person occupied/leftover readout and the Add-to-sprint preview.
   */
  capacity: CapacitySummary;
  /** Total per-member bandwidth reduction from the sprint's common tasks. */
  commonReductionDays: number;
  /**
   * The sprint's subtasks keyed by parent task id (so a parent that was never
   * estimated directly still counts its subtasks' effort as occupied load).
   */
  subtasksByParent: Map<string, Task[]>;
  /** Working days remaining from today to the sprint end. */
  remainingWorkingDays: number;
  /** "Day X of Y" position from the sprint's calendar range. */
  dayInfo: { day: number; total: number };
  /** Committed vs completed effort progress. */
  progress: SprintProgress;
  /** Locally-computed sprint insights (health, effort, capacity, blockers). */
  insights: SprintInsights;
  loading: boolean;
  error: string | null;
  /** True while a board write is in flight. */
  saving: boolean;
  refresh: () => Promise<void>;
  /** Moves a task to a lane. Routed callers should use {@link completeTask} for DONE. */
  moveTask: (task: Task, boardStatus: BoardStatus) => Promise<void>;
  /**
   * Sets a task's board lane inline (no drag). Alias of {@link moveTask}; the
   * page routes a DONE selection through the completion prompt just as a drop
   * into the DONE lane does.
   */
  setTaskStatus: (task: Task, boardStatus: BoardStatus) => Promise<void>;
  /**
   * Adds an existing (backlog) task to THIS board's sprint. When `markAdhoc` is
   * true the task is flagged as unplanned/ad-hoc scope and stamped with the add
   * time.
   */
  addToSprint: (task: Task, markAdhoc?: boolean) => Promise<void>;
  /**
   * Creates a NEW task directly in THIS board's sprint (quick-create with the
   * sprint preset). When `markAdhoc` is true it is recorded as unplanned scope.
   */
  createInSprint: (draft: TaskDraft, markAdhoc?: boolean) => Promise<Task>;
  /** Removes a task from the sprint, returning it to the backlog (task preserved). */
  removeFromSprint: (task: Task) => Promise<void>;
  /**
   * Assigns (or, with an empty string, clears) a task's OWNER inline from the
   * board — the sprint-first "assign later" path. Optimistic: reflects the new
   * owner on the card immediately, then persists and reconciles.
   */
  assignOwner: (task: Task, owner: string) => Promise<void>;
  /** Toggles a task's blocked state inline (quick block/unblock, no modal). */
  toggleBlocked: (task: Task) => Promise<void>;
  /** Sets or clears a task's unplanned/ad-hoc flag. */
  markAdhoc: (task: Task, adhoc: boolean) => Promise<void>;
  /** Sets a task blocked with a structured blocker. */
  setBlocked: (task: Task, blocker: Blocker) => Promise<void>;
  /** Clears a task's blocked state. */
  unblock: (task: Task) => Promise<void>;
  /**
   * Completes a task: moves it to DONE, clears any block, captures actual effort
   * (+ optional note), and SILENTLY records a HistoricalEstimate for the Learn
   * phase. Loads the task's saved estimate for the complexity/extra-work signal.
   */
  completeTask: (
    task: Task,
    actualEffortDays: number,
    note?: string,
  ) => Promise<void>;
  /** Loads a task's saved estimate (for prefilling the completion prompt). */
  loadEstimate: (task: Task) => Promise<TaskEstimate | undefined>;
  /** Starts the sprint (planning → active). */
  startSprint: () => Promise<void>;
  /** Builds the completion summary WITHOUT mutating (for the confirm dialog). */
  previewCompletion: () => Promise<SprintCompletionSummary>;
  /**
   * Completes the sprint: sets status → complete and returns carryover (not-DONE)
   * tasks to the backlog. Returns the final summary.
   */
  completeSprint: () => Promise<SprintCompletionSummary>;
  /** Optional AI narrative over the live insights numbers. */
  narrateInsights: () => Promise<string>;
  /** Optional AI narrative over a completion summary. */
  narrateCompletion: (summary: SprintCompletionSummary) => Promise<string>;
}

export function useSprintBoard(
  sprintId?: string,
): UseSprintBoardResult {
  const api = useApi();
  const { activeRoomId } = useActiveRoom();
  const stores = useMemo(
    () => ({
      sprints: new SprintStore(api),
      tasks: new TaskStore(api),
      teams: new TeamStore(api),
      historical: new HistoricalStore(api),
      estimates: new TaskEstimateStore(api),
    }),
    [api],
  );

  const [sprint, setSprint] = useState<Sprint | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [backlog, setBacklog] = useState<Task[]>([]);
  const [subtaskProgress, setSubtaskProgress] = useState<
    Map<string, SubtaskProgress>
  >(new Map());
  const [subtasksByParent, setSubtasksByParent] = useState<
    Map<string, Task[]>
  >(new Map());
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Resolves which sprint the board should show. */
  const resolveSprint = useCallback(async (): Promise<Sprint | null> => {
    if (sprintId) {
      const loaded = await stores.sprints.get(sprintId);
      return loaded && !loaded.deleted ? loaded : null;
    }
    if (!activeRoomId) return null;
    const all = sortSprintsByRecency(
      await stores.sprints.listByRoom(activeRoomId),
    );
    if (all.length === 0) return null;
    const active = all.find((s) => s.status === 'active');
    if (active) return active;
    const planning = all.find((s) => s.status === 'planning');
    // Prefer a plan-able sprint; otherwise the most recent (e.g. a completed one).
    return planning ?? all[0];
  }, [sprintId, activeRoomId, stores]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resolved = await resolveSprint();
      if (!resolved) {
        setSprint(null);
        setTasks([]);
        setBacklog([]);
        setSubtaskProgress(new Map());
        setSubtasksByParent(new Map());
        setMembers([]);
        return;
      }
      setSprint(resolved);
      // One room-scoped scan feeds BOTH the sprint board and the backlog picker
      // (backlog = room tasks with no sprint; sprint = tasks in this sprint) —
      // mirroring the planning surface, so "Add from backlog" needs no 2nd query.
      const [roomTasks, team] = await Promise.all([
        stores.tasks.listByRoom(resolved.roomId),
        resolved.teamId
          ? stores.teams.get(resolved.teamId)
          : Promise.resolve(undefined),
      ]);
      const sprintTasks = roomTasks.filter((t) => t.sprintId === resolved.id);
      // The board, By-Person view and insights show ONLY top-level tasks;
      // subtasks (which inherit their parent's sprint) are rolled into the
      // parent card's badge instead.
      setTasks(topLevelTasks(sprintTasks));
      setSubtaskProgress(subtaskProgressByParent(sprintTasks));
      const sbp = new Map<string, Task[]>();
      for (const t of sprintTasks) {
        if (!isSubtask(t)) continue;
        const parentId = (t.parentTaskId ?? '').trim();
        if (!parentId) continue;
        const arr = sbp.get(parentId);
        if (arr) arr.push(t);
        else sbp.set(parentId, [t]);
      }
      setSubtasksByParent(sbp);
      setBacklog(topLevelTasks(roomTasks.filter((t) => !t.sprintId)));
      setMembers(team && !team.deleted ? team.members ?? [] : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the sprint board.');
    } finally {
      setLoading(false);
    }
  }, [resolveSprint, stores]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const columns = useMemo<Record<BoardStatus, Task[]>>(() => {
    const cols: Record<BoardStatus, Task[]> = {
      TODO: [],
      IN_PROGRESS: [],
      DONE: [],
    };
    for (const t of tasks) {
      const lane = (t.boardStatus ?? 'TODO') as BoardStatus;
      (cols[lane] ?? cols.TODO).push(t);
    }
    return cols;
  }, [tasks]);

  const workingDays = useMemo(
    () => (sprint ? sprintWorkingDays(sprint.startDate, sprint.endDate) : 0),
    [sprint],
  );

  const commonReductionDays = useMemo(
    () => sumCommonReductionDays(sprint?.commonTasks),
    [sprint],
  );

  // Bandwidth-based capacity over the sprint's top-level tasks. Occupied effort
  // per task uses its TOTAL effort (own custom effort or its subtasks' sum), so
  // subtask-only tasks still count. Per-member capacity is their bandwidth
  // (working days − on-call, ×1.08, − common reduction).
  const capacity = useMemo<CapacitySummary>(() => {
    const capacityMembers: CapacityMember[] = members.map((m) => ({
      alias: m.alias,
      oncallDays: memberOncallDays(sprint?.oncallDaysByAlias, m.alias, workingDays),
    }));
    const effectiveTasks = tasks.map((t) => ({
      owner: t.owner,
      effortDays: totalEffortDays(t, subtasksByParent.get(t.id)),
    }));
    return capacitySummary(
      capacityMembers,
      workingDays,
      effectiveTasks,
      commonReductionDays,
    );
  }, [members, tasks, subtasksByParent, sprint, workingDays, commonReductionDays]);

  const dayInfo = useMemo(
    () => (sprint ? sprintDayInfo(sprint.startDate, sprint.endDate) : { day: 0, total: 0 }),
    [sprint],
  );

  const remainingWorkingDays = useMemo(() => {
    if (!sprint) return 0;
    const today = todayIso();
    if (today < sprint.startDate) return workingDays;
    if (today > sprint.endDate) return 0;
    return sprintWorkingDays(today, sprint.endDate);
  }, [sprint, workingDays]);

  const progress = useMemo(() => sprintProgress(tasks), [tasks]);

  // Active sprint whose start date has already passed → new work is mid-sprint
  // (unplanned) scope. Drives the default ad-hoc flag when adding tasks.
  const sprintStarted = useMemo(
    () =>
      !!sprint &&
      sprint.status === 'active' &&
      sprint.startDate !== '' &&
      sprint.startDate < todayIso(),
    [sprint],
  );

  const insights = useMemo(
    () => buildSprintInsights(tasks, members.length, remainingWorkingDays),
    [tasks, members.length, remainingWorkingDays],
  );

  // ---- Board mutations (optimistic where it helps) ------------------------

  const moveTask = useCallback(
    async (task: Task, boardStatus: BoardStatus) => {
      if ((task.boardStatus ?? 'TODO') === boardStatus) return;
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, boardStatus } : t)),
      );
      setSaving(true);
      try {
        await stores.tasks.moveToBoardStatus(task, boardStatus);
        await refresh();
      } catch (e) {
        await refresh();
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [stores, refresh],
  );

  const setBlocked = useCallback(
    async (task: Task, blocker: Blocker) => {
      setSaving(true);
      try {
        await stores.tasks.setBlocked(task, true, blocker);
        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [stores, refresh],
  );

  const unblock = useCallback(
    async (task: Task) => {
      setSaving(true);
      try {
        await stores.tasks.setBlocked(task, false);
        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [stores, refresh],
  );

  // ---- Inline (drag-free) board actions -----------------------------------

  // Setting a lane inline reuses the exact same optimistic move as drag.
  const setTaskStatus = moveTask;

  const addToSprint = useCallback(
    async (task: Task, markAdhoc = false) => {
      if (!sprint) return;
      setSaving(true);
      try {
        await stores.tasks.addToSprint(task, sprint.id, markAdhoc);
        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [sprint, stores, refresh],
  );

  const createInSprint = useCallback(
    async (draft: TaskDraft, markAdhoc = false): Promise<Task> => {
      if (!sprint) throw new Error('No sprint to add tasks to.');
      setSaving(true);
      try {
        const now = new Date().toISOString();
        const created = await stores.tasks.create(sprint.roomId, {
          ...draft,
          sprintId: sprint.id,
          boardStatus: draft.boardStatus ?? 'TODO',
          adhoc: markAdhoc,
          addedToSprintAt: markAdhoc ? now : undefined,
        });
        await refresh();
        return created;
      } finally {
        setSaving(false);
      }
    },
    [sprint, stores, refresh],
  );

  const removeFromSprint = useCallback(
    async (task: Task) => {
      setSaving(true);
      try {
        await stores.tasks.assignToSprint(task, null);
        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [stores, refresh],
  );

  const assignOwner = useCallback(
    async (task: Task, owner: string) => {
      const trimmed = owner.trim();
      if ((task.owner ?? '') === trimmed) return;
      // Optimistic: reflect the new owner (mirrored to assignee) immediately so
      // the card / By-Person lane updates without a flash, then persist.
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? { ...t, owner: trimmed || undefined, assignee: trimmed || undefined }
            : t,
        ),
      );
      setSaving(true);
      try {
        await stores.tasks.assignOwner(task, trimmed);
        await refresh();
      } catch (e) {
        await refresh(); // revert to server truth on failure
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [stores, refresh],
  );

  const toggleBlocked = useCallback(
    async (task: Task) => {
      setSaving(true);
      try {
        if (task.blocked) {
          await stores.tasks.setBlocked(task, false);
        } else {
          // Quick inline block: reuse any existing blocker, else a minimal one
          // stamped now (the rich reason is captured on the task page).
          await stores.tasks.setBlocked(
            task,
            true,
            task.blocker ?? { reason: 'Blocked', since: new Date().toISOString() },
          );
        }
        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [stores, refresh],
  );

  const markAdhoc = useCallback(
    async (task: Task, adhoc: boolean) => {
      setSaving(true);
      try {
        await stores.tasks.setAdhoc(task, adhoc);
        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [stores, refresh],
  );

  const loadEstimate = useCallback(
    async (task: Task): Promise<TaskEstimate | undefined> => {
      if (!task.estimateId) return undefined;
      return stores.estimates.get(task.estimateId).catch(() => undefined);
    },
    [stores],
  );

  const completeTask = useCallback(
    async (task: Task, actualEffortDays: number, note?: string) => {
      setSaving(true);
      try {
        const estimate = await loadEstimate(task);
        // Move to DONE + capture actual/note first (the visible action)...
        await stores.tasks.complete(task, actualEffortDays, note);
        // ...then SILENTLY record the historical estimate for future AI signal.
        const draft = buildHistoricalDraft(task, estimate, actualEffortDays);
        await stores.historical
          .record(task.roomId, draft)
          .catch(() => undefined); // never fail completion on the learn write
        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [stores, loadEstimate, refresh],
  );

  // ---- Sprint lifecycle ---------------------------------------------------

  const startSprint = useCallback(async () => {
    if (!sprint) return;
    setSaving(true);
    try {
      await stores.sprints.setStatus(sprint, 'active');
      await refresh();
    } finally {
      setSaving(false);
    }
  }, [sprint, stores, refresh]);

  const loadSprintHistory = useCallback(async () => {
    if (!sprint) return [];
    const roomRecords = await stores.historical.listByRoom(sprint.roomId);
    const taskIds = new Set(tasks.map((t) => t.id));
    return roomRecords.filter((r) => taskIds.has(r.taskId));
  }, [sprint, tasks, stores]);

  const previewCompletion =
    useCallback(async (): Promise<SprintCompletionSummary> => {
      const records = await loadSprintHistory();
      return buildSprintCompletion(tasks, records);
    }, [tasks, loadSprintHistory]);

  const completeSprint =
    useCallback(async (): Promise<SprintCompletionSummary> => {
      if (!sprint) throw new Error('No sprint to complete.');
      setSaving(true);
      try {
        const records = await loadSprintHistory();
        const summary = buildSprintCompletion(tasks, records);
        // Return carryover (not-DONE) tasks to the backlog.
        for (const t of summary.carryoverTasks) {
          await stores.tasks.assignToSprint(t, null);
        }
        await stores.sprints.setStatus(sprint, 'complete');
        await refresh();
        return summary;
      } finally {
        setSaving(false);
      }
    }, [sprint, tasks, loadSprintHistory, stores, refresh]);

  const narrateInsights = useCallback(async (): Promise<string> => {
    return insightsNarrative(api.inference, sprint?.name ?? '', insights);
  }, [api, sprint, insights]);

  const narrateCompletion = useCallback(
    async (summary: SprintCompletionSummary): Promise<string> => {
      return completionNarrative(api.inference, sprint?.name ?? '', summary);
    },
    [api, sprint],
  );

  return {
    sprint,
    tasks,
    backlog,
    sprintStarted,
    columns: sprint ? columns : EMPTY_COLUMNS,
    subtaskProgress,
    members,
    workingDays,
    capacity,
    commonReductionDays,
    subtasksByParent,
    remainingWorkingDays,
    dayInfo,
    progress,
    insights,
    loading,
    error,
    saving,
    refresh,
    moveTask,
    setTaskStatus,
    addToSprint,
    createInSprint,
    removeFromSprint,
    assignOwner,
    toggleBlocked,
    markAdhoc,
    setBlocked,
    unblock,
    completeTask,
    loadEstimate,
    startSprint,
    previewCompletion,
    completeSprint,
    narrateInsights,
    narrateCompletion,
  };
}
